import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus, Edit, Calendar, Eye, Search, Loader2, AlertCircle,
  Store, Image, Clock, CheckCircle, XCircle, ChevronRight, Trash2, MessageSquare, Settings,
  Package, ShoppingBag, Megaphone, DollarSign, TrendingUp, Receipt, Bell, Star, MapPin
} from 'lucide-react';
import { useVendor } from '../contexts/VendorContext';
import VendorBadgeList from './VendorBadgeList';
import { vendorAdvertisementApi, preOrderApi, notificationApi } from '../services/api';
import { ProductPreOrder, Notification } from '../types';
import { extractApiError } from '../utils/errorUtils';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { formatDateOnly, parseDateOnly } from '../utils/fairStatus'; // Issue #1672

function VendorDashboardContent() {
  const {
    vendors,
    loadVendors,
    loadReservations,
    reservations,
    deactivateVendor,
    isLoading,
    error,
    clearError,
  } = useVendor();

  // Skip to main content handler for accessibility
  const skipToMainContent = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const mainContent = document.getElementById('vendor-dashboard-main-content');
    if (mainContent) {
      mainContent.focus();
      mainContent.scrollIntoView();
    }
  };

  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Dashboard metrics state
  const [preOrders, setPreOrders] = useState<ProductPreOrder[]>([]);
  const [advertisements, setAdvertisements] = useState<number>(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Feature flag for vendor advertisements
  const { isEnabled: adsEnabled } = useFeatureFlag('vendor_advertisements');

  // Feature flag for vendor financial tracking (Issue #1474)
  const { isEnabled: financialTrackingEnabled } = useFeatureFlag('vendor_financial_tracking');

  // Feature flag for pre-orders (Issue #1468)
  const { isEnabled: preOrdersEnabled } = useFeatureFlag('preorders_enabled');

  // Load vendors on mount
  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Load reservations when a vendor is selected
  useEffect(() => {
    if (selectedVendorId) {
      loadReservations(selectedVendorId);
    }
  }, [selectedVendorId, loadReservations]);

  // Select first vendor by default
  useEffect(() => {
    if (vendors.length > 0 && !selectedVendorId) {
      setSelectedVendorId(vendors[0].id);
    }
  }, [vendors, selectedVendorId]);

  // Load dashboard metrics when vendor is selected
  useEffect(() => {
    const loadDashboardMetrics = async () => {
      if (!selectedVendorId) return;

      try {
        setMetricsLoading(true);

        // Load pre-orders for the vendor (only if feature flag is enabled)
        if (preOrdersEnabled) {
          const preOrdersData = await preOrderApi.getVendorPreOrders(selectedVendorId);
          setPreOrders(preOrdersData.items);
        }

        // Load advertisement count (only if feature flag is enabled)
        if (adsEnabled) {
          const adsData = await vendorAdvertisementApi.getAdvertisements({ page: 1, pageSize: 1 });
          setAdvertisements(adsData.total);
        }

        // Load notifications
        const notificationsData = await notificationApi.getNotifications(5, 0);
        setNotifications(notificationsData.notifications);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load dashboard metrics:', apiError);
      } finally {
        setMetricsLoading(false);
      }
    };

    loadDashboardMetrics();
  }, [selectedVendorId, adsEnabled, preOrdersEnabled]);

  const selectedVendor = vendors.find(v => v.id === selectedVendorId);

  const handleDeactivate = async (vendorId: number) => {
    try {
      setIsDeleting(true);
      await deactivateVendor(vendorId);
      setShowDeleteConfirm(null);
      if (selectedVendorId === vendorId) {
        setSelectedVendorId(vendors.find(v => v.id !== vendorId)?.id || null);
      }
    } catch {
      // Error handled by context
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'confirmed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-primary-dark rounded-full text-xs">
            <CheckCircle className="w-3 h-3" />
            Confirmed
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
            <XCircle className="w-3 h-3" />
            Cancelled
          </span>
        );
      default:
        return null;
    }
  };

  const confirmedReservations = reservations.filter(r => r.status === 'confirmed');
  const pendingPreOrders = preOrders.filter(po => po.status === 'pending' || po.status === 'confirmed');

  // Get upcoming fairs (next 5 with confirmed reservations)
  const upcomingFairs = confirmedReservations
    .filter(r => {
      const start = parseDateOnly(r.fair_start_date);
      return start ? start >= new Date() : false;
    })
    .sort((a, b) => {
      const aTime = parseDateOnly(a.fair_start_date)?.getTime() ?? 0;
      const bTime = parseDateOnly(b.fair_start_date)?.getTime() ?? 0;
      return aTime - bTime;
    })
    .slice(0, 5);

  if (isLoading && vendors.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Skip Navigation Link for Accessibility */}
      <a
        href="#vendor-dashboard-main-content"
        onClick={skipToMainContent}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Skip to main content
      </a>

      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-3 mb-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-base sm:text-xl font-bold text-gray-900" data-testid="vendor-dashboard-heading">Vendor Dashboard</h1>
          <p className="text-xs sm:text-sm text-gray-600">Manage your vendor profiles and reservations</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to="/vendor/profile/new"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm"
              data-testid="new-vendor-profile-button"
            >
              <Plus className="h-4 w-4 flex-shrink-0" />
              <span>New Vendor Profile</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" data-testid="vendor-error-alert">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
          <button onClick={clearError} className="text-red-500 hover:text-red-700" data-testid="vendor-dashboard-close-error-button">
            &times;
          </button>
        </div>
      )}

      {/* No Vendors State */}
      {vendors.length === 0 && !isLoading && (
        <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-200" data-testid="vendor-empty-state">
          <Store className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No vendor profiles yet</h2>
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            Create a vendor profile to start booking slots at fairs and showcasing your products.
          </p>
          <Link
            to="/vendor/profile/new"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark"
            data-testid="create-first-profile-button"
          >
            <Plus className="w-5 h-5" />
            Create Your First Profile
          </Link>
        </div>
      )}

      {/* Dashboard Content */}
      {vendors.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Vendor List Sidebar */}
          <div className="lg:col-span-1" role="complementary" aria-label="Vendor profiles list">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-testid="vendor-profiles-list">
              <div className="p-4 border-b border-gray-200">
                <h2 className="font-semibold text-gray-900">Your Profiles</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {vendors.map(vendor => (
                  <button
                    key={vendor.id}
                    onClick={() => setSelectedVendorId(vendor.id)}
                    className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                      selectedVendorId === vendor.id ? 'bg-teal-50 border-l-4 border-primary' : ''
                    }`}
                    data-testid="vendor-profile-item"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        {vendor.portfolio_images && vendor.portfolio_images.length > 0 ? (
                          <img
                            src={vendor.portfolio_images[0]}
                            alt=""
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <Store className="w-5 h-5 text-secondary" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{vendor.business_name}</p>
                        <p className="text-sm text-gray-500">
                          {vendor.product_types?.length || 0} product types
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div
            id="vendor-dashboard-main-content"
            className="lg:col-span-3 space-y-6"
            role="main"
            tabIndex={-1}
          >
            {selectedVendor && (
              <>
                {/* Business Summary Metrics */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="business-summary-section" role="region" aria-labelledby="business-summary-heading">
                  <h2 id="business-summary-heading" className="text-lg font-semibold text-gray-900 mb-4">Business Summary</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    <div className="flex flex-col" data-testid="active-reservations-stat">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-teal-100 rounded-lg">
                          <Calendar className="w-4 h-4 text-secondary" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-gray-900" data-testid="active-reservations-count">{confirmedReservations.length}</p>
                      <p className="text-sm text-gray-500">Active Reservations</p>
                    </div>
                    <div className="flex flex-col" data-testid="upcoming-fairs-stat">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-moonbone rounded-lg">
                          <MapPin className="w-4 h-4 text-secondary" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-gray-900" data-testid="upcoming-fairs-count">{upcomingFairs.length}</p>
                      <p className="text-sm text-gray-500">Upcoming Fairs</p>
                    </div>
                    {preOrdersEnabled && (
                      <div className="flex flex-col" data-testid="pending-preorders-stat">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-2 bg-orange-100 rounded-lg">
                            <ShoppingBag className="w-4 h-4 text-orange-600" />
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-gray-900" data-testid="pending-preorders-count">
                          {metricsLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : pendingPreOrders.length}
                        </p>
                        <p className="text-sm text-gray-500">Pending Pre-Orders</p>
                      </div>
                    )}
                    {adsEnabled && (
                      <div className="flex flex-col" data-testid="active-ads-stat">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-2 bg-pink-100 rounded-lg">
                            <Megaphone className="w-4 h-4 text-pink-600" />
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-gray-900" data-testid="active-ads-count">
                          {metricsLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : advertisements}
                        </p>
                        <p className="text-sm text-gray-500">Active Ads</p>
                      </div>
                    )}
                    <div className="flex flex-col" data-testid="portfolio-images-stat">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Image className="w-4 h-4 text-purple-600" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-gray-900" data-testid="images-count">
                        {selectedVendor.portfolio_images?.length || 0}
                      </p>
                      <p className="text-sm text-gray-500">Portfolio Images</p>
                    </div>
                    <div className="flex flex-col" data-testid="average-rating-stat">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-2 bg-yellow-100 rounded-lg">
                          <Star className="w-4 h-4 text-yellow-600" />
                        </div>
                      </div>
                      <p className="text-2xl font-bold text-gray-900" data-testid="average-rating">
                        {selectedVendor.average_rating ? selectedVendor.average_rating.toFixed(1) : 'N/A'}
                      </p>
                      <p className="text-sm text-gray-500">Average Rating</p>
                    </div>
                  </div>
                </div>

                {/* Notifications & Alerts */}
                {notifications.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="notifications-section">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Bell className="w-5 h-5 text-secondary" />
                        Recent Notifications
                      </h2>
                      <Link
                        to="/notifications"
                        className="text-sm text-secondary hover:text-primary-dark"
                        data-testid="view-all-notifications-link"
                      >
                        View All
                      </Link>
                    </div>
                    <div className="space-y-3">
                      {notifications.slice(0, 3).map(notification => (
                        <div
                          key={notification.id}
                          className={`p-3 rounded-lg border ${
                            notification.read
                              ? 'bg-gray-50 border-gray-200'
                              : 'bg-moonbone border-teal-200'
                          }`}
                          data-testid="notification-item"
                        >
                          <div className="flex items-start gap-3">
                            <Bell className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                              notification.read ? 'text-gray-400' : 'text-secondary'
                            }`} />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${
                                notification.read ? 'text-gray-700' : 'text-gray-900 font-medium'
                              }`}>{notification.message}</p>
                              {notification.created_at && (
                                <p className="text-xs text-gray-500 mt-1">
                                  {new Date(notification.created_at).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upcoming Fairs */}
                {upcomingFairs.length > 0 && (
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="upcoming-fairs-section">
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Fairs</h2>
                    <div className="space-y-4">
                      {upcomingFairs.map(reservation => (
                        <div
                          key={reservation.id}
                          className="p-4 border border-gray-200 rounded-lg hover:border-teal-300 transition-colors"
                          data-testid="upcoming-fair-item"
                        >
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            <div className="flex-1">
                              <h3 className="font-medium text-gray-900" data-testid="fair-name">
                                {reservation.fair_name}
                              </h3>
                              <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {formatDateOnly(reservation.fair_start_date)}
                                  {reservation.fair_end_date && ` - ${formatDateOnly(reservation.fair_end_date)}`}
                                </span>
                                {reservation.fair_location && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-4 h-4" />
                                    {reservation.fair_location}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-2">
                                {getStatusBadge(reservation.status)}
                                {reservation.slot_number && (
                                  <span className="text-sm text-gray-600">
                                    Booth #{reservation.slot_number}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Link
                                to={`/fairs/${reservation.fair_id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                                data-testid="view-fair-details-button"
                              >
                                <Eye className="w-4 h-4" />
                                View Details
                              </Link>
                              <Link
                                to={`/vendor/${selectedVendor.id}/product-catalog?fairId=${reservation.fair_id}`}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark"
                                data-testid="manage-products-button"
                              >
                                <Package className="w-4 h-4" />
                                Manage Products
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Profile Actions */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="vendor-profile-card">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900" data-testid="vendor-business-name">{selectedVendor.business_name}</h2>
                    <div className="flex gap-2">
                      <Link
                        to={`/vendors/${selectedVendor.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                        data-testid="view-public-profile-button"
                      >
                        <Eye className="w-4 h-4" />
                        View Public
                      </Link>
                      <Link
                        to={`/vendor/profile/${selectedVendor.id}/edit`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-dark"
                        data-testid="edit-profile-button"
                      >
                        <Edit className="w-4 h-4" />
                        Edit
                      </Link>
                      <button
                        onClick={() => setShowDeleteConfirm(selectedVendor.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                        data-testid="deactivate-profile-button"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {selectedVendor.description && (
                    <p className="text-gray-600 mb-4">{selectedVendor.description}</p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {selectedVendor.product_types?.map(type => (
                      <span key={type} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                        {type}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="vendor-quick-actions">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Link
                      to="/"
                      className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid="find-fairs-link"
                    >
                      <Search className="w-6 h-6 text-secondary" />
                      <div>
                        <p className="font-medium text-gray-900">Find Fairs</p>
                        <p className="text-sm text-gray-500">Browse available fairs</p>
                      </div>
                    </Link>
                    <Link
                      to={`/vendor/${selectedVendor.id}/booth-preferences`}
                      className="flex items-center gap-3 p-4 border border-teal-200 bg-moonbone rounded-lg hover:bg-moonbone transition-colors"
                      data-testid="booth-preferences-link"
                    >
                      <Settings className="w-6 h-6 text-secondary" />
                      <div>
                        <p className="font-medium text-gray-900">Booth Preferences</p>
                        <p className="text-sm text-gray-500">Get smart recommendations</p>
                      </div>
                    </Link>
                    <Link
                      to="/vendor/inquiries"
                      className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid="my-inquiries-link"
                    >
                      <MessageSquare className="w-6 h-6 text-secondary" />
                      <div>
                        <p className="font-medium text-gray-900">My Inquiries</p>
                        <p className="text-sm text-gray-500">View messages</p>
                      </div>
                    </Link>
                    <Link
                      to={`/vendor/profile/${selectedVendor.id}/edit`}
                      className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid="add-photos-link"
                    >
                      <Image className="w-6 h-6 text-purple-600" />
                      <div>
                        <p className="font-medium text-gray-900">Add Photos</p>
                        <p className="text-sm text-gray-500">Update your portfolio</p>
                      </div>
                    </Link>
                    <Link
                      to={`/vendors/${selectedVendor.id}`}
                      className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid="preview-profile-link"
                    >
                      <Eye className="w-6 h-6 text-secondary" />
                      <div>
                        <p className="font-medium text-gray-900">Preview Profile</p>
                        <p className="text-sm text-gray-500">See public view</p>
                      </div>
                    </Link>
                    <Link
                      to={`/vendor/${selectedVendor.id}/product-catalog`}
                      className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      data-testid="product-catalog-link"
                    >
                      <Package className="w-6 h-6 text-green-600" />
                      <div>
                        <p className="font-medium text-gray-900">Product Catalog</p>
                        <p className="text-sm text-gray-500">Manage fair products</p>
                      </div>
                    </Link>
                    {preOrdersEnabled && (
                      <Link
                        to="/vendor/preorders"
                        className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid="preorders-link"
                      >
                        <ShoppingBag className="w-6 h-6 text-orange-600" />
                        <div>
                          <p className="font-medium text-gray-900">Pre-Orders</p>
                          <p className="text-sm text-gray-500">Manage custom orders</p>
                        </div>
                      </Link>
                    )}
                    {adsEnabled && (
                      <Link
                        to="/vendor/advertisements"
                        className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid="advertisements-link"
                      >
                        <Megaphone className="w-6 h-6 text-pink-600" />
                        <div>
                          <p className="font-medium text-gray-900">Advertisements</p>
                          <p className="text-sm text-gray-500">Manage ads</p>
                        </div>
                      </Link>
                    )}
                    {confirmedReservations.length > 0 && financialTrackingEnabled && (
                      <>
                        <Link
                          to={`/vendor/fairs/${confirmedReservations[0].fair_id}/revenue`}
                          className="flex items-center gap-3 p-4 border border-green-200 bg-green-50 rounded-lg hover:bg-green-100 transition-colors"
                          data-testid="revenue-tracking-link"
                        >
                          <DollarSign className="w-6 h-6 text-green-600" />
                          <div>
                            <p className="font-medium text-gray-900">Revenue Tracking</p>
                            <p className="text-sm text-gray-500">Track fair income</p>
                          </div>
                        </Link>
                        <Link
                          to={`/vendor/fairs/${confirmedReservations[0].fair_id}/expenses`}
                          className="flex items-center gap-3 p-4 border border-red-200 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                          data-testid="expense-tracking-link"
                        >
                          <Receipt className="w-6 h-6 text-red-600" />
                          <div>
                            <p className="font-medium text-gray-900">Expense Tracking</p>
                            <p className="text-sm text-gray-500">Track fair costs</p>
                          </div>
                        </Link>
                        <Link
                          to={`/vendor/fairs/${confirmedReservations[0].fair_id}/roi-tracker`}
                          className="flex items-center gap-3 p-4 border border-teal-200 bg-teal-50 rounded-lg hover:bg-secondary-light/100 transition-colors"
                          data-testid="roi-tracker-link"
                        >
                          <TrendingUp className="w-6 h-6 text-secondary" />
                          <div>
                            <p className="font-medium text-gray-900">ROI Tracker</p>
                            <p className="text-sm text-gray-500">Analyze profitability</p>
                          </div>
                        </Link>
                      </>
                    )}
                  </div>
                </div>

                {/* Achievement Badges (Issue #684, #955) */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="vendor-badges-section">
                  <VendorBadgeList vendorId={selectedVendor.id} />
                </div>

                {/* Recent Reservations */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-testid="vendor-reservations-list">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900">Reservations</h2>
                  </div>
                  {reservations.length === 0 ? (
                    <div className="text-center py-8" data-testid="reservations-empty-state">
                      <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-500">No reservations yet</p>
                      <p className="text-sm text-gray-400">
                        Start by finding a fair and booking a slot
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {reservations.slice(0, 5).map(reservation => (
                        <div
                          key={reservation.id}
                          className="flex items-center justify-between p-4 border border-gray-100 rounded-lg"
                          data-testid="reservation-item"
                        >
                          <div>
                            <p className="font-medium text-gray-900" data-testid="reservation-fair-name">{reservation.fair_name}</p>
                            <p className="text-sm text-gray-500">
                              Slot {reservation.slot_number} &bull; ${reservation.price_paid}
                            </p>
                          </div>
                          {getStatusBadge(reservation.status)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="deactivate-confirm-modal">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Deactivate Profile?</h3>
            <p className="text-gray-600 mb-6">
              This will hide your vendor profile from public view. You can reactivate it later.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                data-testid="cancel-deactivate-button"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeactivate(showDeleteConfirm)}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                data-testid="confirm-deactivate-button"
              >
                {isDeleting ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Export the content component directly - VendorProvider wrapping is done in App.tsx for consistency
export default VendorDashboardContent;
